import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Sparkles, TrendingUp, Heart, Crown, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { BookCard, GenreCard, TropeCard, Carousel, SectionHeader, EmptyState } from "@/components/cards";
import { useApp } from "@/context/AppContext";
import RecRow from "@/components/RecRow";

export default function Home() {
  const { user } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/homepage").then((r) => setData(r.data)).catch(() => {}); }, [user]);

  if (!data) return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Loading…</div>;
  const s = data.settings || {};
  const hasBooks = data.trending.length || data.new_releases.length;

  return (
    <div className="fade-in">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {s.hero_image && <img src={s.hero_image} alt="" className="h-full w-full object-cover object-center" />}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="track-label text-xs text-primary flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> {s.label || "Stories you'll fall for"}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05] mt-5">
              {s.headline || "Every story has a"}{" "}
              <span className="text-primary text-glow italic">{s.highlight || "world"}.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-lg">{s.description}</p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/discover" data-testid="hero-start-reading" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">
                <BookOpen className="h-5 w-5" /> Start Reading Free
              </Link>
              <Link to="/premium" data-testid="hero-go-premium" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border hover:border-primary transition-colors font-medium">
                <Sparkles className="h-4 w-4 text-primary" /> Go Premium
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 space-y-16 mt-4">
        {/* Continue reading */}
        {data.continue_reading?.length > 0 && (
          <section>
            <SectionHeader icon={BookOpen} title="Continue Reading" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.continue_reading.slice(0, 3).map((b) => (
                <Link key={b.id} to={`/read/${b.slug}/${b.progress?.current_chapter || 1}`} className="flex gap-4 p-4 rounded-2xl border border-border surface-2 card-hover" data-testid={`continue-${b.slug}`}>
                  <img src={b.cover} alt="" className="h-24 w-16 object-cover rounded-lg" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-semibold truncate">{b.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">Chapter {b.progress?.current_chapter || 1}</p>
                    <div className="h-1.5 bg-secondary rounded-full mt-3 overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${b.progress?.percent || 0}%` }} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">{b.progress?.percent || 0}% read</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Trending */}
        {data.trending.length > 0 && (
          <section>
            <SectionHeader icon={TrendingUp} title="Trending Now" to="/discover?trending=1" />
            <Carousel testid="trending-carousel">
              {data.trending.map((b) => <div key={b.id} className="w-40 md:w-48 shrink-0 snap-start"><BookCard book={b} /></div>)}
            </Carousel>
          </section>
        )}

        {/* Genres */}
        {data.genres.length > 0 && (
          <section>
            <SectionHeader title="Explore by Genre" to="/genres" />
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {data.genres.slice(0, 8).map((g) => <GenreCard key={g.id} genre={g} />)}
            </div>
          </section>
        )}

        {/* New releases */}
        <section>
          <SectionHeader icon={Sparkles} title="New Releases" to="/discover" />
          {data.new_releases.length ? (
            <Carousel testid="new-releases-carousel">
              {data.new_releases.map((b) => <div key={b.id} className="w-40 md:w-48 shrink-0 snap-start"><BookCard book={b} /></div>)}
            </Carousel>
          ) : <EmptyState title="New stories are coming soon." subtitle="Check back shortly for fresh releases." />}
        </section>

        {/* Tropes */}
        {data.tropes.length > 0 && (
          <section>
            <SectionHeader icon={Heart} title="Popular Tropes" to="/tropes" />
            <Carousel testid="tropes-carousel">
              {data.tropes.slice(0, 12).map((t) => <div key={t.id} className="w-56 shrink-0 snap-start"><TropeCard trope={t} /></div>)}
            </Carousel>
          </section>
        )}

        {/* Recommendations */}
        <RecRow endpoint="/recommendations?limit=12" title="Popular Picks for You" personalTitle="Recommended for You" testid="home-recommendations" />

        {!hasBooks && <EmptyState title="New stories are coming soon." subtitle="Our shelves are being stocked. Come back soon." />}

        {/* Premium CTA */}
        <section className="relative overflow-hidden rounded-3xl border border-border p-8 md:p-12 surface-2">
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <Crown className="h-10 w-10 text-primary shrink-0" />
              <div>
                <h2 className="text-2xl md:text-3xl font-display font-bold"><span className="text-primary">Go Premium</span> for the ultimate reading experience</h2>
                <p className="text-muted-foreground mt-2">Ad-free reading, early access to new chapters, exclusive stories and more.</p>
              </div>
            </div>
            <Link to="/premium" data-testid="premium-cta" className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">
              Try Premium Now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
